import { Check, ChevronDown, X } from "lucide-react";
import { useMemo, useState } from "react";

import { CopyButton } from "@/components/CopyButton.tsx";
import { UploadYourFirstFileSection } from "@/components/UploadYourFirstFileSection.tsx";
import { Button } from "@/components/ui/button";
import { Drawer, DrawerContent, DrawerTitle } from "@/components/ui/drawer";
import {
  type ClientGuide,
  MCP_CLIENT_BUCKETS,
  type McpClientBucket,
  buildClientGuide,
  isClientConnected,
  sortClients,
} from "@/lib/mcpClients.ts";
import { type OnboardingState, markGenericClient } from "@/lib/onboarding.ts";

// FRE-290 wired the empty vaul shell; FRE-294 fills section 2 ("Connect a
// client"). FRE-293 wires section 1 ("Upload your first file") via the
// extracted `UploadYourFirstFileSection` component — same component is also
// used inline as the file-list empty-state card.
//
// Non-modal + dismissible + right-direction so a user can alt-tab to a
// terminal mid-setup without losing the drawer. `modal={false}` keeps the
// page behind interactive; `overlay={false}` on DrawerContent suppresses the
// default backdrop dim so the page-behind stays visually live too.

export type DrawerMeOrg = { id: string; slug: string; name: string };

export function GetStartedDrawer({
  open,
  onOpenChange,
  onboarding: initialOnboarding,
  organizations,
  activeOrganizationId,
}: {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  onboarding: OnboardingState | null;
  organizations: DrawerMeOrg[];
  activeOrganizationId: string | null;
}) {
  // Local copy so the Generic self-mark flip is reflected immediately without
  // round-tripping the loader. Parent will refetch on next loader run.
  const [onboarding, setOnboarding] = useState<OnboardingState | null>(initialOnboarding);

  // Re-sync the local view if the parent passes a fresh row (loader refetch).
  // We intentionally compare by updatedAt — content equality would also work
  // but is more expensive on every drawer mount.
  if (initialOnboarding && initialOnboarding.updatedAt !== onboarding?.updatedAt) {
    setOnboarding(initialOnboarding);
  }

  // `window` is always present on a rendered drawer (vaul mounts to portal,
  // browser-only). Falls back to the localhost dev value so SSR snapshots
  // (none today) don't crash.
  const host = typeof window !== "undefined" ? window.location.origin : "https://drop.example";

  const orderedBuckets = useMemo(() => sortClients(MCP_CLIENT_BUCKETS, onboarding), [onboarding]);

  return (
    <Drawer open={open} onOpenChange={onOpenChange} direction="right" modal={false} dismissible>
      <DrawerContent
        overlay={false}
        aria-describedby={undefined}
        className="!p-0 sm:!max-w-[480px] before:hidden"
      >
        <div className="flex h-full flex-col rounded-none border-l border-border bg-popover text-popover-foreground shadow-xl">
          <div className="flex items-center justify-between border-b border-border px-5 py-3">
            <DrawerTitle className="text-sm font-semibold text-foreground">Get started</DrawerTitle>
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              onClick={() => onOpenChange(false)}
              aria-label="Close Get started drawer"
            >
              <X className="size-4" />
            </Button>
          </div>
          <div className="flex-1 overflow-y-auto px-5 py-5">
            <UploadYourFirstFileSection
              organizations={organizations}
              defaultOrganizationId={activeOrganizationId}
              host={host}
              variant="drawer"
            />
            <ConnectClientSection
              host={host}
              onboarding={onboarding}
              orderedBuckets={orderedBuckets}
              onGenericMarked={(next) => setOnboarding(next)}
            />
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  );
}

function ConnectClientSection({
  host,
  onboarding,
  orderedBuckets,
  onGenericMarked,
}: {
  host: string;
  onboarding: OnboardingState | null;
  orderedBuckets: readonly McpClientBucket[];
  onGenericMarked: (next: OnboardingState) => void;
}) {
  return (
    <section>
      <h2 className="text-sm font-semibold text-foreground">Connect a client</h2>
      <p className="mt-1 text-xs text-muted-foreground">
        Drop ships an MCP server. Pick your tool — we'll detect the connection on the first call.
      </p>
      <ul className="mt-3 space-y-2">
        {orderedBuckets.map((bucket) => (
          <li key={bucket}>
            <ClientCard
              host={host}
              bucket={bucket}
              connected={isClientConnected(bucket, onboarding)}
              onGenericMarked={onGenericMarked}
            />
          </li>
        ))}
      </ul>
    </section>
  );
}

function ClientCard({
  host,
  bucket,
  connected,
  onGenericMarked,
}: {
  host: string;
  bucket: McpClientBucket;
  connected: boolean;
  onGenericMarked: (next: OnboardingState) => void;
}) {
  // Collapse by default to avoid drowning the drawer in code blocks. Connected
  // clients stay collapsed too — they're done, the snippet is reference-only.
  const [expanded, setExpanded] = useState(false);
  const guide = useMemo(() => buildClientGuide(bucket, host), [bucket, host]);
  const panelId = `mcp-client-panel-${bucket}`;

  return (
    <div className="rounded-md border border-border bg-card">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        aria-controls={panelId}
        className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left hover:bg-muted"
      >
        <div className="flex min-w-0 items-center gap-2">
          <StatusPill connected={connected} />
          <span className="truncate text-sm font-medium text-foreground">{guide.label}</span>
        </div>
        <ChevronDown
          aria-hidden="true"
          className={`size-3 shrink-0 text-muted-foreground transition-transform ${expanded ? "rotate-180" : ""}`}
        />
      </button>
      {expanded ? (
        <div id={panelId} className="border-t border-border px-3 py-3 text-sm">
          <ClientGuideBody guide={guide} />
          {bucket === "generic" ? (
            <GenericSelfMark connected={connected} onMarked={onGenericMarked} />
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function ClientGuideBody({ guide }: { guide: ClientGuide }) {
  return (
    <>
      <p className="text-xs text-foreground/90">{guide.headline}</p>
      {guide.installLink ? (
        <Button
          size="xs"
          className="mt-2"
          render={<a href={guide.installLink.href}>{guide.installLink.label}</a>}
        />
      ) : null}
      {guide.target ? (
        <p className="mt-2 font-mono text-[11px] text-muted-foreground">{guide.target}</p>
      ) : null}
      <div className="mt-1.5 flex items-start gap-2">
        <pre className="flex-1 overflow-x-auto rounded-md border border-border bg-muted px-2.5 py-2 text-[11px] leading-snug text-foreground">
          <code data-language={guide.language}>{guide.snippet}</code>
        </pre>
        <CopyButton value={guide.snippet} label="Copy" size="xs" />
      </div>
      <a
        href={guide.docsUrl}
        target="_blank"
        rel="noreferrer noopener"
        className="mt-2 inline-block text-[11px] font-medium text-primary hover:underline"
      >
        Open the full guide →
      </a>
    </>
  );
}

function GenericSelfMark({
  connected,
  onMarked,
}: {
  connected: boolean;
  onMarked: (next: OnboardingState) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (connected) {
    return (
      <p className="mt-3 text-xs text-foreground/80">
        Marked as set up. The other clients tick automatically on first call.
      </p>
    );
  }

  async function handleClick() {
    setBusy(true);
    setError(null);
    try {
      const next = await markGenericClient();
      onMarked(next);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mt-3">
      <Button
        type="button"
        variant="outline"
        size="xs"
        onClick={() => void handleClick()}
        disabled={busy}
      >
        {busy ? "Saving…" : "I've set this up"}
      </Button>
      {error ? <p className="mt-1 text-xs text-destructive">{error}</p> : null}
    </div>
  );
}

function StatusPill({ connected }: { connected: boolean }) {
  if (connected) {
    return (
      <span
        aria-label="Connected"
        title="Connected"
        className="inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary"
      >
        <Check className="size-2.5" strokeWidth={3} />
      </span>
    );
  }
  return (
    <span
      aria-label="Not connected"
      title="Not connected yet"
      className="inline-block h-2 w-2 shrink-0 rounded-full bg-muted-foreground/40"
    />
  );
}
